/**
 * Entry point. Order matters here more than it usually does.
 *
 * `installLucide()` runs **before** React renders, because `design/components/icon/Icon.jsx`
 * reads `window.lucide` at render time and falls back to an empty box of the right size if
 * it is missing. That fallback is deliberate and silent, so a late install produces a UI
 * with no icons and no error — `assertLucideLoaded()` is what turns that into a stack trace
 * instead of a mystery.
 *
 * `styles.css` is imported once, here, at the root. It is the design system's single entry
 * point — ten `@import` lines of tokens — and every component below reads its CSS variables.
 * Importing it anywhere else, or more than once, is how a token ends up defined twice.
 *
 * `app.css` follows it, and the order is the point: `src/design/` is vendored and uneditable
 * (`design/VENDOR.md`), so the app layer's own classes — and the handful of tokens waiting to
 * be folded upstream — have to load after it to win. It is also where every `@media` and
 * `:focus-visible` rule lives, because the screens' inline `style` objects cannot hold one.
 */

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './design/styles.css';
import './app.css';
import { App } from './App';
import { isUnauthenticated } from './api/errors';
import { sessionLost } from './auth/sessionLost';
import { assertLucideLoaded, installLucide } from './lucide';

installLucide();
assertLucideLoaded();

/**
 * The single 401 handler (task 30). Attached to both caches, because a session can end
 * between any two requests and a mutation is as likely to be the one that discovers it as a
 * query — `AuthN-AuthZ#sessions` lists four ways it happens and all four are server-side.
 *
 * Here rather than in each screen: fourteen screens each handling this is fourteen chances
 * to forget one, and the one forgotten is a screen still showing data from a session that no
 * longer exists. Anything added later inherits it without knowing it exists.
 */
const onError = (error: unknown): void => {
  if (isUnauthenticated(error)) sessionLost();
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      // `Non-Functional-Requirements.md` and the design language agree on one rule that
      // outranks caching convenience: never show stale data as fresh. Refetching on focus
      // is how an operator who has been away from the tab for an hour is not shown an hour
      // -old number with no indication of its age.
      refetchOnWindowFocus: true,
      // No automatic retry. A failed request has to be visible: this UI's job during an
      // incident is to say what is broken, and three silent retries is three seconds of a
      // spinner where the answer should be an error panel.
      retry: false,
      staleTime: 0,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('index.html has no #root element');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
